/**
 * Люди, техника и материалы акта допработ строками (ПР-20) — как в отчёте смены: профессия и человеко-часы,
 * машина проекта и машино-часы, материал со склада проекта и количество. Без ставок и сумм.
 */
import type { ExtraWorkActDetail, ExtraWorkLabor, ExtraWorkMachine, ExtraWorkMaterial } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Field, Input, Select } from '../../ui/form';

export interface ResourceDraft {
  labor: ExtraWorkLabor[];
  machines: ExtraWorkMachine[];
  materials: ExtraWorkMaterial[];
}

export const emptyResources = (): ResourceDraft => ({ labor: [], machines: [], materials: [] });

export const resourcesOf = (a: ExtraWorkActDetail): ResourceDraft => ({
  labor: (a.labor ?? []).map((x) => ({ ...x })),
  machines: (a.machines ?? []).map((x) => ({ machineId: x.machineId, hours: x.hours })),
  materials: (a.materials ?? []).map((x) => ({ itemId: x.itemId, quantity: x.quantity })),
});

/** В запрос — только непустые списки: сервер прежней версии их не знает, а пустой список ничего не добавляет */
export const nonEmptyResources = (r: ResourceDraft): Partial<ResourceDraft> =>
  Object.fromEntries(Object.entries(r).filter(([, v]) => v.length)) as Partial<ResourceDraft>;

const NUM = /^(0|[1-9]\d{0,14})(\.\d{1,6})?$/;

/** Ввод с запятой — как привыкли; на сервер уходит с точкой */
const num = (s: string) => s.trim().replace(',', '.');

/** Все ли строки заполнены так, что сервер их примет */
export function resourcesValid(r: ResourceDraft): boolean {
  const unique = (ids: string[]) => new Set(ids).size === ids.length;
  return (
    r.labor.every(
      (x) =>
        x.profession.trim() && x.headcount >= 1 && NUM.test(x.hours) && Number(x.hours) <= x.headcount * 24,
    ) &&
    r.machines.every((x) => x.machineId && NUM.test(x.hours) && Number(x.hours) <= 24) &&
    r.materials.every((x) => x.itemId && NUM.test(x.quantity) && Number(x.quantity) > 0) &&
    unique(r.machines.map((x) => x.machineId)) &&
    unique(r.materials.map((x) => x.itemId))
  );
}

export function ResourceEditor({
  projectId,
  value,
  onChange,
  act,
}: {
  projectId: string;
  value: ResourceDraft;
  onChange: (v: ResourceDraft) => void;
  /** Исправляемый акт: его машины и материалы остаются в списках выбора, даже если их нет среди доступных сейчас */
  act?: ExtraWorkActDetail;
}) {
  const me = useMe();
  const machines = useQuery({
    queryKey: ['site', me.tenant?.id, 'project-machines', projectId],
    queryFn: () =>
      api<Array<{ machineId: string; name: string; regNumber: string | null }>>(
        `/site/projects/${projectId}/machines`,
      ),
    enabled: Boolean(me.tenant?.staff && projectId),
  });
  const materials = useQuery({
    queryKey: ['site', me.tenant?.id, 'project-materials', projectId],
    queryFn: () =>
      api<{ items: Array<{ itemId: string; name: string; unit: string }> }>(
        `/site/projects/${projectId}/materials`,
      ),
    enabled: Boolean(me.tenant?.staff && projectId),
  });
  const items = [
    ...(act?.materials ?? []).map((x) => ({ itemId: x.itemId, name: x.name, unit: x.unit })),
    ...(materials.data?.items ?? []),
  ].filter((x, i, all) => all.findIndex((y) => y.itemId === x.itemId) === i);
  const machineOptions = [
    ...(act?.machines ?? []).map((x) => ({ machineId: x.machineId, name: x.name, regNumber: x.regNumber })),
    ...(machines.data ?? []),
  ].filter((x, i, all) => all.findIndex((y) => y.machineId === x.machineId) === i);
  const set = <K extends keyof ResourceDraft>(k: K, rows: ResourceDraft[K]) =>
    onChange({ ...value, [k]: rows });

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h3 className="font-medium text-[14px] text-ink">Люди</h3>
        {value.labor.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки без своего номера, порядок задаёт человек
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 sm:grid-cols-[2fr_1fr_1fr_auto]">
            <Field label="Профессия" className="col-span-3 sm:col-span-1">
              <Input
                className="min-h-11"
                value={row.profession}
                onChange={(e) =>
                  set(
                    'labor',
                    value.labor.map((x, j) => (j === i ? { ...x, profession: e.target.value } : x)),
                  )
                }
                placeholder="Землекоп"
              />
            </Field>
            <Field label="Человек">
              <Input
                className="min-h-11"
                inputMode="numeric"
                value={String(row.headcount)}
                onChange={(e) =>
                  set(
                    'labor',
                    value.labor.map((x, j) =>
                      j === i
                        ? { ...x, headcount: Math.max(0, Number.parseInt(e.target.value, 10) || 0) }
                        : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Чел.-ч всего">
              <Input
                className="min-h-11"
                inputMode="decimal"
                value={row.hours}
                onChange={(e) =>
                  set(
                    'labor',
                    value.labor.map((x, j) => (j === i ? { ...x, hours: num(e.target.value) } : x)),
                  )
                }
              />
            </Field>
            <RemoveButton
              label={`Убрать строку «${row.profession || 'люди'}»`}
              onClick={() =>
                set(
                  'labor',
                  value.labor.filter((_, j) => j !== i),
                )
              }
            />
          </div>
        ))}
        <AddButton
          onClick={() => set('labor', [...value.labor, { profession: '', headcount: 1, hours: '8' }])}
          label="Добавить людей"
        />
      </section>

      <section className="space-y-2">
        <h3 className="font-medium text-[14px] text-ink">Техника</h3>
        {machines.data && !machineOptions.length ? (
          <p className="text-[13px] text-ink-2">
            На проекте нет действующей техники — опишите её в примечании.
          </p>
        ) : null}
        {value.machines.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки без своего номера, порядок задаёт человек
          <div key={i} className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[3fr_1fr_auto]">
            <Field label="Машина" className="col-span-2 sm:col-span-1">
              <Select
                className="min-h-11"
                value={row.machineId}
                onChange={(e) =>
                  set(
                    'machines',
                    value.machines.map((x, j) => (j === i ? { ...x, machineId: e.target.value } : x)),
                  )
                }
              >
                <option value="">Выберите машину</option>
                {machineOptions.map((m) => (
                  <option key={m.machineId} value={m.machineId}>
                    {m.name}
                    {m.regNumber ? ` · ${m.regNumber}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Маш.-ч">
              <Input
                className="min-h-11"
                inputMode="decimal"
                value={row.hours}
                onChange={(e) =>
                  set(
                    'machines',
                    value.machines.map((x, j) => (j === i ? { ...x, hours: num(e.target.value) } : x)),
                  )
                }
              />
            </Field>
            <RemoveButton
              label="Убрать машину"
              onClick={() =>
                set(
                  'machines',
                  value.machines.filter((_, j) => j !== i),
                )
              }
            />
          </div>
        ))}
        <AddButton
          onClick={() => set('machines', [...value.machines, { machineId: '', hours: '8' }])}
          label="Добавить технику"
        />
      </section>

      <section className="space-y-2">
        <h3 className="font-medium text-[14px] text-ink">Материалы</h3>
        {materials.data && !items.length ? (
          <p className="text-[13px] text-ink-2">
            На складах проекта нет остатков — опишите материал в примечании.
          </p>
        ) : null}
        {value.materials.map((row, i) => {
          const unit = items.find((x) => x.itemId === row.itemId)?.unit;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: строки без своего номера, порядок задаёт человек
            <div key={i} className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[3fr_1fr_auto]">
              <Field label="Материал" className="col-span-2 sm:col-span-1">
                <Select
                  className="min-h-11"
                  value={row.itemId}
                  onChange={(e) =>
                    set(
                      'materials',
                      value.materials.map((x, j) => (j === i ? { ...x, itemId: e.target.value } : x)),
                    )
                  }
                >
                  <option value="">Выберите материал</option>
                  {items.map((m) => (
                    <option key={m.itemId} value={m.itemId}>
                      {m.name}, {m.unit}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={unit ? `Кол-во, ${unit}` : 'Количество'}>
                <Input
                  className="min-h-11"
                  inputMode="decimal"
                  value={row.quantity}
                  onChange={(e) =>
                    set(
                      'materials',
                      value.materials.map((x, j) => (j === i ? { ...x, quantity: num(e.target.value) } : x)),
                    )
                  }
                />
              </Field>
              <RemoveButton
                label="Убрать материал"
                onClick={() =>
                  set(
                    'materials',
                    value.materials.filter((_, j) => j !== i),
                  )
                }
              />
            </div>
          );
        })}
        <AddButton
          onClick={() => set('materials', [...value.materials, { itemId: '', quantity: '' }])}
          label="Добавить материал"
        />
      </section>
    </div>
  );
}

function AddButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button type="button" variant="ghost" className="min-h-11" onClick={onClick}>
      <Plus className="size-4" /> {label}
    </Button>
  );
}

function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      className="mt-6 min-h-11 min-w-11 self-start"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Trash2 className="size-4" />
    </Button>
  );
}

/** Ресурсы в карточке акта: то, что подписывает заказчик */
export function ResourceList({ act }: { act: ExtraWorkActDetail }) {
  const labor = act.labor ?? [];
  const machines = act.machines ?? [];
  const materials = act.materials ?? [];
  if (!labor.length && !machines.length && !materials.length) return null;
  return (
    <div className="space-y-3">
      {labor.length ? (
        <div>
          <dt className="text-ink-2">Люди</dt>
          <dd>
            <ul className="mt-1 space-y-1">
              {labor.map((x, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: порядок строк акта не меняется на экране
                <li key={i}>
                  {x.profession} — {x.headcount} чел., {x.hours} чел.-ч
                </li>
              ))}
            </ul>
          </dd>
        </div>
      ) : null}
      {machines.length ? (
        <div>
          <dt className="text-ink-2">Техника</dt>
          <dd>
            <ul className="mt-1 space-y-1">
              {machines.map((x) => (
                <li key={x.machineId}>
                  {x.name}
                  {x.regNumber ? ` · ${x.regNumber}` : ''} — {x.hours} маш.-ч
                </li>
              ))}
            </ul>
          </dd>
        </div>
      ) : null}
      {materials.length ? (
        <div>
          <dt className="text-ink-2">Материалы</dt>
          <dd>
            <ul className="mt-1 space-y-1">
              {materials.map((x) => (
                <li key={x.itemId}>
                  {x.code} {x.name} — {x.quantity} {x.unit}
                </li>
              ))}
            </ul>
          </dd>
        </div>
      ) : null}
    </div>
  );
}
